import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// The unified site header/footer, same as /news, /download and the rest. This
// page used the legacy header.js + components/footer, which depend on a theme
// stylesheet that was never committed and so render off on a dark page.
import SeoHeader from "../shared/SeoHeader";
import SeoFooter from "../shared/SeoFooter";
import "./agents.css";
import { X } from "lucide-react";
import { chatApi } from "../../services/socialApi";
// Carry what the prerenderer fetched into the client's FIRST render, so both
// sides agree and hydration is not thrown away. See utils/prerenderData.js —
// /hive, /news, the carousel and InstallCounter already do this; this gallery
// never did, which is why it alone hydrated twice and left an orphaned footer.
import { publishPrerenderData, readPrerenderData } from "../../utils/prerenderData";

import AgentPoster from "../../assets/images/AgentPoster.png";
// The build-time registry scripts/fetch-agents.js maintains. It exists so the
// per-agent pages can be prerendered, and it is the honest fallback when the
// live list is unreachable: 32 agents with 24 distinct images and a real
// tagline each, sitting in the bundle.
import agentRegistry from "../../data/agents.json";

// Mirror of scripts/fetch-agents.js slugify — the /agents/:slug/about pages are
// derived from that registry, so the card link must slugify names identically
// or it will 404. Keep the two in sync.
function agentSlug(name) {
  return String(name || '')
    .toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 72);
}


const parseAgentData = (result) => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.prompts)) return result.prompts;
  if (Array.isArray(result?.agents)) return result.agents;
  if (Array.isArray(result?.results)) return result.results;
  return [];
};

/**
 * The gallery is fed by more than one backend and they do not agree on names.
 *
 * The cloud list (/getprompt_all/) uses image_url + prompt. A local Nunba's
 * curated list (/prompts) uses avatar + description, so on a local install
 * every card fell back to the same placeholder poster and the same "An AI agent
 * ready to help" line -- the real avatars and descriptions were sitting right
 * there under different keys.
 */
/**
 * Collapse the many words the backends use for provenance into three places an
 * agent can actually do its work.
 *
 * HARTOS nodes report a tier ('flat' / 'regional' / 'central' — see the
 * superadmin node report), the curated catalogue says type 'local' | 'cloud',
 * and the raw records say source 'local'. They all mean one of:
 *   local   — this machine
 *   peer    — someone else's HARTOS/Nunba node (flat or regional)
 *   central — the hosted hive
 */
const ORIGIN_LABELS = {
  local: "Runs on your machine",
  peer: "Runs on a peer node",
  central: "Runs on the hive",
  // A fourth kind, and the one the catalogue used to be blind to: agents you
  // own on the hive that are not published. They are yours, they are not
  // public, and the gallery fetched no source that could return them.
  private: "Yours, not published",
};

function normalizeOrigin(raw) {
  const v = String(raw || "").toLowerCase().trim();
  if (!v) return "central";
  if (v === "local" || v === "device" || v === "on-device") return "local";
  if (v === "private" || v === "mine" || v === "owned") return "private";
  if (v === "flat" || v === "regional" || v === "peer" || v === "node") return "peer";
  return "central"; // 'cloud', 'central', anything hosted
}

const normalizeAgent = (a) => ({
  ...a,
  name: a.name ? String(a.name).trim() : "",
  // `image` is the key scripts/fetch-agents.js writes into
  // src/data/agents.json. Without it the bundled registry normalises to an
  // empty image_url and every card falls back to the same poster, which is
  // the fourth spelling of this same field and the reason the gallery looked
  // like one agent repeated.
  image_url: a.image_url || a.avatar || a.teacher_image_url || a.image || "",
  video_text: a.video_text || a.description || a.capability || a.tagline || "",
  // WHERE THIS AGENT DOES ITS WORK. Three places, not two:
  //   local   — on this machine (a Nunba built-in; nothing leaves the device)
  //   peer    — on another HARTOS node, flat or regional tier
  //   central — the hosted hive
  // Take the record's own word for it when it has one (`type`, `source`,
  // `tier`, `node`), and only fall back to where we fetched it from.
  origin: normalizeOrigin(a.origin || a.type || a.source || a.tier || a.node),
});

/**
 * The same agent can now arrive from several sources, so collapse on identity.
 *
 * LOCAL WINS. If an agent is available on this machine that is the copy to
 * offer: it answers without the network and its data never leaves the device.
 * A peer node beats the hive for the same reason, one step removed. Only the
 * losing copy's missing art/description are borrowed, so a local agent keeps
 * running locally while still showing a picture the hive happened to have.
 */
const ORIGIN_RANK = { private: 0, local: 1, peer: 2, central: 3 };

const dedupeAgents = (rows) => {
  const byKey = new Map();
  rows.forEach((r) => {
    const key = String(r.prompt_id ?? r.id ?? r.name).trim().toLowerCase();
    const seen = byKey.get(key);
    if (!seen) {
      byKey.set(key, r);
      return;
    }
    const rRank = ORIGIN_RANK[r.origin] ?? 2;
    const seenRank = ORIGIN_RANK[seen.origin] ?? 2;
    // Closer to the user wins outright; keep the loser's art/copy if the
    // winner lacks it.
    const winner = rRank < seenRank ? r : seen;
    const loser = winner === r ? seen : r;
    byKey.set(key, {
      ...winner,
      image_url: winner.image_url || loser.image_url || "",
      video_text: winner.video_text || loser.video_text || "",
    });
  });
  return Array.from(byKey.values());
};

/**
 * Keep only agents meant to be browsed.
 *
 * The AUTHORITY here is the backend flag. Cloud records carry is_public /
 * create_agent and this honours them. A local Nunba's raw dump does not: all
 * 1157 records come back with is_public absent, source "local", and no
 * create_agent, so nothing distinguishes a published agent from a storage row.
 *
 * The name patterns below are therefore a floor, not a solution. Measured
 * against a real local dump they take 1159 down to 370, and internal records
 * still survive (f5_tts_probe_heal_v2, "Agent 13") because a name cannot tell
 * you what a record is FOR. The fix is for the local backend to publish
 * is_public the way the cloud does; until then prefer the curated /prompts
 * list, which returns the 7 real agents with their avatars and descriptions.
 */
const isBrowsableAgent = (a) => {
  if (!a || !a.name) return false;
  // An unpublished agent is hidden UNLESS it is yours. The catalogue is meant
  // to show all three kinds -- public hive, your private hive, and local -- and
  // dropping every is_public === false record made the middle one unreachable
  // even once a source returned it. `origin: "private"` is only ever set for
  // rows that came back from the current user's own list.
  if (a.is_public === false && a.origin !== "private") return false;
  if (a.create_agent === true) return false;    // a builder, not a listing
  if (a.is_active === false) return false;
  const name = String(a.name).trim();
  if (!name) return false;
  // Storage-row shapes, not published agents: "<digits>_personality",
  // "<digits>_0_3", "Agent <digits>", maintenance tasks and probe/heal runs.
  if (/^\d{4,}(_|$)/.test(name)) return false;
  if (/^agent\s+\d+$/i.test(name)) return false;
  if (/^fix_/i.test(name)) return false;
  if (/_(probe|heal|test|debug)(_|$)/i.test(name)) return false;
  if (/_v\d+$/i.test(name)) return false;       // versioned internal runs
  return true;
};

const Agents = ({
  isOverlay = false,
  onClose = () => {},
  onAgentSelect = () => {},
  predefinedAgents = null,
}) => {
  // Seed from what the prerenderer saved. Read in a useState INITIALISER so it
  // lands in the first render: read from an effect and it arrives one render
  // late, which is the mismatch this exists to remove.
  // Seed order: what the prerenderer captured, else the bundled registry.
  //
  // Measured on the live page 2026-08-19: __HEVOLVE_PRERENDER_DATA__ carried no
  // agentsList key at all, so the saved HTML shipped with no agents and no
  // images -- zero <img>, zero background-image, zero svg -- while
  // src/data/agents.json sat in the same bundle with 32 agents and 24 distinct
  // pictures. The list fetch does not resolve during capture, and there was no
  // fallback, so the most visual page on the site prerendered as text.
  //
  // Starting from a bundled constant is also strictly better for hydration
  // than starting from []: the capture and the client's first render begin
  // from the same value by construction, rather than from an empty array the
  // capture then fills in.
  const registrySeed = Array.isArray(agentRegistry)
    ? agentRegistry.map(normalizeAgent).filter((a) => a.name)
    : [];
  const seededAgents = readPrerenderData("agentsList") || (registrySeed.length ? registrySeed : null);
  const [agentsData, setAgentsData] = useState(seededAgents || []);
  const [loading, setLoading] = useState(!seededAgents);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredAgents, setFilteredAgents] = useState(seededAgents || []);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        if (predefinedAgents && predefinedAgents.length > 0) {
          const validAgents = dedupeAgents(
            predefinedAgents.map(normalizeAgent).filter(isBrowsableAgent),
          );

          setAgentsData(validAgents);
          setFilteredAgents(validAgents);
          setLoading(false);
          return;
        }

        // ONE catalogue, from every source that answers.
        //
        // This used to be a fallback chain: try one endpoint, and only if it
        // came back empty try the next. That made the listing depend on which
        // backend you happened to be on — a local Nunba showed its handful of
        // built-ins and never the public agents, the website showed the public
        // agents and never the built-ins, and each was invisible to the other.
        //
        // Ask all three at once instead, tag every record with where it came
        // from, and merge. A source that is missing (the website has no local
        // backend) or slow simply contributes nothing; allSettled means one
        // failure cannot take the page down with it.
        // Read inside the effect, never during render: this component is
        // prerendered and a localStorage read on the first pass is the exact
        // mismatch that cost the /social pages their hydrated subtrees.
        let ownerId = null;
        try {
          ownerId = localStorage.getItem("hevolve_access_id") || null;
        } catch { ownerId = null; }

        const sources = [
          { origin: "local", fn: () => chatApi.getCatalogPrompts("all") },
          { origin: "local", fn: () => chatApi.getPublicPrompts() },
          { origin: "cloud", fn: () => chatApi.getPublicPromptsCloud() },
          // The fourth kind. Every source above returns PUBLIC agents, so a
          // signed-in person could not see their own unpublished hive agents
          // anywhere in the catalogue: the endpoint existed
          // (getUserPromptsCloud) and nothing called it. Only added when there
          // is somebody to ask for, so a logged-out visitor and the
          // prerenderer both skip it and the captured HTML stays public-only.
          ...(ownerId
            ? [{ origin: "private", fn: () => chatApi.getUserPromptsCloud(ownerId) }]
            : []),
        ];

        const settled = await Promise.allSettled(sources.map((s) => s.fn()));
        let data = [];
        settled.forEach((res, i) => {
          const { origin } = sources[i];
          if (res.status !== "fulfilled") {
            console.warn(`Agents.js: source ${i} (${origin}) unavailable:`, res.reason?.message);
            return;
          }
          const rows = parseAgentData(res.value);
          console.log(`Agents.js: source ${i} (${origin}) returned:`, rows.length);
          // Provenance: prefer what the record says about itself (the curated
          // catalogue carries type: 'local' | 'cloud'), else where we fetched it.
          data = data.concat(
            // `private` wins over whatever the record says about itself: the
            // row came back from THIS user's own list, and that is a fact about
            // who can see it, not about where it runs.
            rows.map((r) => ({
              ...r,
              origin: origin === "private" ? "private" : (r.type || r.source || origin),
            })),
          );
        });

        if (data.length === 0) {
          toast.error("Failed to load agents. Please try again later.");
        }

        if (data.length > 0) {
          const validAgents = dedupeAgents(
            data.map(normalizeAgent).filter(isBrowsableAgent),
          );

          const byOrigin = validAgents.reduce((acc, a) => {
            acc[a.origin] = (acc[a.origin] || 0) + 1;
            return acc;
          }, {});
          console.log(
            "Agents.js: Valid agents to render:",
            validAgents.length,
            "(of",
            data.length,
            "returned) by origin:",
            byOrigin,
          );
          // Publish for the prerenderer to bake into the saved HTML, so the
          // next visitor's first render starts from exactly what the capture
          // shows. A fetch result that does not change afterwards, so
          // publishing once at completion is the right place (see
          // utils/prerenderData.js).
          publishPrerenderData("agentsList", validAgents);
          setAgentsData(validAgents);
          setFilteredAgents(validAgents);
        } else {
          console.warn("Agents.js: No agents data found after parsing.");
        }
      } catch (error) {
        console.error("Agents.js: Unexpected error:", error);
        toast.error("Something went wrong while loading agents.");
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, [predefinedAgents]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    const filtered = agentsData.filter((agent) =>
      agent.name.trim().toLowerCase().includes(query.trim().toLowerCase()),
    );
    setFilteredAgents(filtered);
  };

  if (loading) {
    return (
      <div className={isOverlay ? "text-white text-center p-8" : ""}>
        Loading...
      </div>
    );
  }

  if (isOverlay) {
    return (
      <div className="fixed inset-0 w-full bg-black bg-opacity-80 z-50 flex justify-center items-center overflow-y-auto">
        <div className="bg-gray-900 rounded-lg shadow-lg max-h-[95vh] flex flex-col">
          <div className="flex justify-between items-center p-4 border-b border-gray-700">
            <h2 className="text-xl font-bold text-white">All Agents</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white rounded-full p-1 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="flex justify-center m-4">
            <input
              type="text"
              placeholder="Search agents..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="p-2 rounded-md border border-gray-300 w-1/2 text-black"
            />
          </div>

          {/* Agents Grid */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="agents-grid">
              {filteredAgents.length === 0 ? (
                <div className="no-agents-message text-white">
                  No agents match your search. Please try a different query.
                </div>
              ) : (
                filteredAgents.map((agent, index) => (
                  <AgentCard
                    key={index}
                    agent={agent}
                    isOverlay={true}
                    onSelect={() => onAgentSelect(agent)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Regular standalone view
  return (
    <>
      {/* Same ground as the /agents/<slug>/about pages this gallery links to:
          --al-bg (#0b1120) under the two translucent radial washes from
          .al-hero::before in pages/Agents/agentLanding.css — violet at 22% top
          right, emerald at 18% mid left. Kept as a fixed background so the tint
          stays put while the grid scrolls, instead of the flat #212A31. */}
      <div style={{ background: "#0b1120" }}>
        <SeoHeader />

        <div
          className="min-h-screen pt-10"
          style={{
            background:
              "radial-gradient(60% 55% at 78% 18%, rgba(139, 92, 246, 0.22), transparent 70%)," +
              "radial-gradient(55% 50% at 12% 30%, rgba(16, 185, 129, 0.18), transparent 70%)," +
              "#0b1120",
            backgroundAttachment: "fixed, fixed, scroll",
          }}
        >
          <div className="text-center mb-8 px-4">
            <h1
              style={{
                fontWeight: 800,
                fontSize: "clamp(1.9rem, 5vw, 3rem)",
                background: "linear-gradient(135deg, #6C63FF, #FF6B6B)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                // A gradient clipped to the text has no descender/ascender
                // slack, so the glyphs get cut at the box edges. Give the line
                // its own height and padding instead of relying on defaults.
                lineHeight: 1.25,
                padding: "0.15em 0",
                margin: 0,
              }}
            >
              Hevolve AI agents
            </h1>
            <p style={{ color: "rgba(229,237,255,0.62)", marginTop: 8 }}>
              Every agent runs live in your browser. No signup, no API key.
            </p>
          </div>

          {/* Search Bar */}
          <div className="flex justify-center mb-8 px-4">
            <input
              type="text"
              placeholder="Search agents..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="p-2 rounded-md w-full md:w-1/2"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.14)",
                color: "#e5edff",
                outline: "none",
              }}
            />
          </div>

          <div className="agents-grid">
            {filteredAgents.length === 0 ? (
              <div className="no-agents-message">
                No agents match your search. Please try a different query.
              </div>
            ) : (
              filteredAgents.map((agent, index) => (
                <AgentCard key={index} agent={agent} isOverlay={false} />
              ))
            )}
          </div>
        </div>
      </div>

      <SeoFooter />
    </>
  );
};

const AgentCard = ({ agent, isOverlay = false, onSelect = () => {} }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const handleButtonClick = () => {
    if (isOverlay) {
      onSelect(agent);
      return;
    }

    const agentName = agent.name.replace(/\s+/g, "-");
    navigate(`/agents/${agentName}`, {
      state: {
        agentData: agent,
      },
    });
  };

  return (
    <div
      onClick={handleButtonClick}
      style={{ backgroundColor: "#212A31", border: "1px solid grey" }}
      className="relative flex flex-col overflow-hidden bg-white dark:bg-slate-900 rounded-md shadow dark:shadow-gray-800 w-64"
    >
      <div
        style={{ backgroundColor: "#212A31" }}
        className="pb-0 relative overflow-hidden after:content-[''] after:absolute after:inset-0 after:mx-auto after:w-72 after:h-72 after:rounded-full"
      >
        <img
          src={
            agent.teacher_image_url
              ? agent.teacher_image_url
              : agent.image_url
                ? agent.image_url
                : AgentPoster
          }
          className="relative rounded-t-md shadow-md dark:shadow-slate-700 z-1 object-cover w-full"
          alt={agent.name}
        />
      </div>
      <div className="px-6 pt-4 pb-12 flex flex-col justify-center items-center relative">
        <h3 className="font-bold text-xl mb-2 text-center text-white">
          {agent.name}
        </h3>

        <h3 className="text-slate-400 text-center line-clamp-1">
          {agent.video_text || "Agent description goes here."}
        </h3>

        {/* Provenance: where this agent actually does its work. The catalogue is
            merged from this machine, peer HARTOS nodes and the hive, so say
            which one a card came from rather than presenting them as one pool.
            Local is the one worth advertising — it answers offline and the
            data never leaves the device. */}
        <span
          title={ORIGIN_LABELS[agent.origin] || ORIGIN_LABELS.central}
          style={{
            marginTop: 6,
            fontSize: "0.68rem",
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            padding: "2px 8px",
            borderRadius: 999,
            color:
              agent.origin === "private"
                ? "#f0b23a"
                : agent.origin === "local"
                  ? "#17c07f"
                  : agent.origin === "peer"
                    ? "#34d3ff"
                    : "rgba(229,237,255,0.55)",
            background:
              agent.origin === "private"
                ? "rgba(240,178,58,0.12)"
                : agent.origin === "local"
                  ? "rgba(23,192,127,0.12)"
                  : agent.origin === "peer"
                    ? "rgba(52,211,255,0.12)"
                    : "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.10)",
          }}
        >
          {agent.origin === "private"
            ? "Yours, unpublished"
            : agent.origin === "local"
              ? "On your machine"
              : agent.origin === "peer"
                ? "Peer node"
                : "Hive"}
        </span>

        {/* The earning path, said once, on the cards it applies to.
            A local or unpublished agent can be published to the hive and paid
            for the usage it serves. That is the whole proposition of /earn and
            it was nowhere near the place a person is actually looking at the
            agent they built. Not shown on hive cards, which are already
            published. */}
        {(agent.origin === "local" || agent.origin === "private") && (
          <a
            href="/earn"
            onClick={(e) => e.stopPropagation()}
            className="mt-1 text-amber-400 hover:text-amber-300 text-xs font-semibold underline-offset-2 hover:underline"
          >
            Publish it to the hive and earn &rarr;
          </a>
        )}

        {/* Extra affordance: a real <a href> to the agent's capability page.
            A genuine link (not onClick-navigate) so crawlers follow it and the
            /about pages get discovered. "Talk To Agent" below is unchanged. */}
        <a
          href={`/agents/${agentSlug(agent.name)}/about`}
          onClick={(e) => e.stopPropagation()}
          className="mt-2 text-emerald-400 hover:text-emerald-300 text-sm font-semibold underline-offset-2 hover:underline"
        >
          More info →
        </a>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleButtonClick();
        }}
        className="bg-blue-500 text-center w-full flex justify-center items-center hover:bg-blue-700 absolute bottom-0 left-1/2 -translate-x-1/2 text-white font-bold py-2 px-4 rounded transition-transform duration-300 hover:scale-110"
      >
        Talk To Agent
      </button>
    </div>
  );
};

export default Agents;
